import React, {useState} from 'react';
import {Chip, IconButton, Menu, MenuItem, Stack, Tooltip, Typography, useMediaQuery} from "@mui/material";
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import {NavLink, useLocation} from "react-router";
import NavItem from "./navItem";
import AuthSelect from "./AuthSelect";
import {useSession} from "../firebase/UserProvider";
import {useAppVersion} from "../hooks/useAppVersion";
import {formatDate} from "../utils/formatTimestamp";
import {isMainNavItemActive, MAIN_NAV_BURGER_MEDIA_QUERY, MAIN_NAV_TAB_FONT_SIZE} from "./mainNavLayout";

const AWP_BASE = process.env.REACT_APP_AWP_API_BASE || '';
const ADMIN_URL = `${AWP_BASE}/me/control/dashboard`;

const Navigation = ({navLinks}) => {
    const {roles} = useSession();
    const isAdmin = Array.isArray(roles) && roles.includes('admin');
    const appVersion = useAppVersion();
    const {pathname} = useLocation();
    const useBurger = useMediaQuery(MAIN_NAV_BURGER_MEDIA_QUERY, {noSsr: true});
    const [menuAnchor, setMenuAnchor] = useState(null);
    const menuOpen = Boolean(menuAnchor);

    const closeMenu = () => setMenuAnchor(null);

    return (
        <Stack
            px={2}
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"stretch"}
            sx={{
                bgcolor: 'white',
                borderBottom: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 4px 0 rgba(0,0,0,0.06)',
                minHeight: 64,
                zIndex: 1,
                flexWrap: 'nowrap',
            }}
        >
            <Stack direction={"row"} alignItems={"center"} spacing={3} sx={{minWidth: 0, flexWrap: 'nowrap'}}>
                <Tooltip title={'Discovery, Analysis, Summaries & Highlights'} arrow={true}>
                    <img src={`${process.env.PUBLIC_URL}/assets/dash-logo.svg`} alt="DASH" width={32} height={32} style={{display: 'block', cursor: 'default', flexShrink: 0}} />
                </Tooltip>

                {useBurger ? (
                    <>
                        <IconButton
                            id="main-nav-menu-button"
                            aria-label="Open main navigation"
                            aria-controls={menuOpen ? 'main-nav-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={menuOpen}
                            onClick={(event) => setMenuAnchor(event.currentTarget)}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="main-nav-menu"
                            anchorEl={menuAnchor}
                            open={menuOpen}
                            onClose={closeMenu}
                            MenuListProps={{'aria-labelledby': 'main-nav-menu-button'}}
                        >
                            {navLinks.map((item) => (
                                <MenuItem
                                    key={item.destination}
                                    component={NavLink}
                                    to={item.destination}
                                    onClick={closeMenu}
                                    selected={isMainNavItemActive(pathname, item)}
                                    sx={{textDecoration: 'none'}}
                                >
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                ) : (
                    <Stack
                        direction={"row"}
                        alignItems={"stretch"}
                        spacing={1}
                        sx={{
                            flexWrap: 'nowrap',
                            whiteSpace: 'nowrap',
                            fontSize: MAIN_NAV_TAB_FONT_SIZE,
                        }}
                    >
                        {navLinks.map((item, index) => (
                            <NavItem
                                key={index}
                                destination={item.destination}
                                label={item.label}
                                activePrefixes={item.activePrefixes}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>

            <Stack direction={"row"} alignItems={"center"} spacing={2} sx={{flexShrink: 0}}>
                {isAdmin && (
                    <Tooltip title="Open admin panel" arrow>
                        <Chip
                            component="a"
                            href={ADMIN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            icon={<AdminPanelSettingsOutlinedIcon />}
                            label="Admin"
                            size="small"
                            clickable
                            sx={{
                                fontWeight: 600,
                                bgcolor: 'error.50',
                                color: 'error.700',
                                borderColor: 'error.200',
                                border: '1px solid',
                                '& .MuiChip-icon': {color: 'error.600'},
                                '&:hover': {bgcolor: 'error.100'},
                            }}
                        />
                    </Tooltip>
                )}
                {isAdmin && appVersion && (
                    <Stack
                        alignItems="flex-end"
                        sx={{cursor: 'default', userSelect: 'none', lineHeight: 1}}
                    >
                        <Typography sx={{fontFamily: 'monospace', fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1.3}}>
                            v{appVersion.version}
                        </Typography>
                        {appVersion.buildDate && (
                            <Typography sx={{fontFamily: 'monospace', fontSize: '0.6rem', color: 'text.disabled', opacity: 0.75, lineHeight: 1.3}}>
                                {formatDate(appVersion.buildDate)}
                            </Typography>
                        )}
                    </Stack>
                )}
                <AuthSelect />
            </Stack>
        </Stack>
    );
};

export default Navigation;
